import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PlusCircle, Download, KeyRound } from 'lucide-react'
import TextField from '@/components/common/TextField'
import RecoveryPhrase from '@/components/RecoveryPhrase'
import RecoveryPhraseInput from '@/components/RecoveryPhraseInput'
import { generateMnemonic } from '@/utils/wallet'
import { useWallet } from '@/hooks/useWallet'
import Snackbar, { SnackbarType } from '@/components/Snackbar'

type Mode = 'choose' | 'create-reveal' | 'create-confirm' | 'import-mnemonic' | 'import-key'

const AddWallet = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { addWallet, loading } = useWallet()

  const [mode, setMode] = useState<Mode>('choose')
  const [name, setName] = useState('')
  const [mnemonic, setMnemonic] = useState('')
  const [isMnemonicValid, setIsMnemonicValid] = useState(false)
  const [importedMnemonic, setImportedMnemonic] = useState('')
  const [isImportedMnemonicValid, setIsImportedMnemonicValid] = useState(false)
  const [privateKey, setPrivateKey] = useState('')
  const [snackbar, setSnackbar] = useState<{ isOpen: boolean; message: string; type: SnackbarType }>({
    isOpen: false,
    message: '',
    type: 'info'
  })

  const handleValidationChange = useCallback((valid: boolean, value: string) => {
    setIsMnemonicValid(valid)
    if (valid) setMnemonic((prev) => prev || value)
  }, [])

  const handleImportValidationChange = useCallback((valid: boolean, value: string) => {
    setIsImportedMnemonicValid(valid)
    setImportedMnemonic(value)
  }, [])

  const startCreate = () => {
    setMnemonic(generateMnemonic())
    setMode('create-reveal')
  }

  const finish = async (kind: 'import-mnemonic' | 'import-key', data: string) => {
    try {
      await addWallet(kind, data, name.trim() || t('wallets.default_name'))
      navigate('/dashboard', { replace: true })
    } catch (error) {
      setSnackbar({
        isOpen: true,
        message: error instanceof Error ? error.message : t('wallets.add_error'),
        type: 'error'
      })
    }
  }

  return (
    <div className="flex flex-col w-full max-w-lg mx-auto gap-8 pt-4">
      <div className="hidden lg:flex items-center gap-4">
        <h1 className="text-3xl font-bold text-text-main">{t('wallets.add_title')}</h1>
      </div>

      {mode === 'choose' && (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <label className="font-semibold text-lg text-text-main">{t('wallets.name_label')}</label>
            <TextField
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('wallets.name_placeholder')}
            />
          </div>

          <div className="flex flex-col gap-3">
            <button
              onClick={startCreate}
              className="flex items-center gap-4 bg-card border border-border-base rounded-2xl p-5 hover:border-primary/30 transition-colors text-left"
            >
              <PlusCircle className="w-6 h-6 icon-gradient shrink-0" />
              <div className="flex flex-col">
                <span className="font-bold text-text-main">{t('wallets.create_new')}</span>
                <span className="text-sm text-text-secondary">{t('wallets.create_new_desc')}</span>
              </div>
            </button>

            <button
              onClick={() => setMode('import-mnemonic')}
              className="flex items-center gap-4 bg-card border border-border-base rounded-2xl p-5 hover:border-primary/30 transition-colors text-left"
            >
              <Download className="w-6 h-6 icon-gradient shrink-0" />
              <div className="flex flex-col">
                <span className="font-bold text-text-main">{t('wallets.import_phrase')}</span>
                <span className="text-sm text-text-secondary">{t('wallets.import_phrase_desc')}</span>
              </div>
            </button>

            <button
              onClick={() => setMode('import-key')}
              className="flex items-center gap-4 bg-card border border-border-base rounded-2xl p-5 hover:border-primary/30 transition-colors text-left"
            >
              <KeyRound className="w-6 h-6 icon-gradient shrink-0" />
              <div className="flex flex-col">
                <span className="font-bold text-text-main">{t('wallets.import_key')}</span>
                <span className="text-sm text-text-secondary">{t('wallets.import_key_desc')}</span>
              </div>
            </button>
          </div>
        </div>
      )}

      {mode === 'create-reveal' && (
        <div className="flex flex-col gap-6">
          <p className="text-text-secondary leading-7">{t('registration.step2_desc')}</p>
          <RecoveryPhrase mnemonic={mnemonic} isRevealed={false} />
          <button
            onClick={() => setMode('create-confirm')}
            className="w-full btn-gradient-fill rounded-full items-center py-4 font-bold"
          >
            {t('registration.next_step')}
          </button>
        </div>
      )}

      {mode === 'create-confirm' && (
        <div className="flex flex-col gap-6">
          <RecoveryPhraseInput
            expectedMnemonic={mnemonic}
            onValidationChange={handleValidationChange}
            showValidation={true}
          />
          <button
            onClick={() => finish('import-mnemonic', mnemonic)}
            disabled={!isMnemonicValid || loading}
            className="w-full btn-gradient-fill rounded-full items-center py-4 font-bold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('wallets.add_title')}
          </button>
        </div>
      )}

      {mode === 'import-mnemonic' && (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <label className="font-semibold text-lg text-text-main">{t('wallets.name_label')}</label>
            <TextField
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('wallets.name_placeholder')}
            />
          </div>
          <RecoveryPhraseInput
            onValidationChange={handleImportValidationChange}
            showValidation={true}
            title={t('welcome.enter_phrase')}
          />
          <button
            onClick={() => finish('import-mnemonic', importedMnemonic)}
            disabled={!isImportedMnemonicValid || loading}
            className="w-full btn-gradient-fill rounded-full items-center py-4 font-bold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('wallets.add_title')}
          </button>
        </div>
      )}

      {mode === 'import-key' && (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <label className="font-semibold text-lg text-text-main">{t('wallets.name_label')}</label>
            <TextField
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('wallets.name_placeholder')}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="font-semibold text-lg text-text-main">{t('recover.key_label')}</label>
            <TextField
              type="password"
              showPasswordToggle
              value={privateKey}
              onChange={(e) => setPrivateKey(e.target.value)}
              placeholder="0x..."
            />
          </div>
          <button
            onClick={() => finish('import-key', privateKey.trim())}
            disabled={!privateKey.trim() || loading}
            className="w-full btn-gradient-fill rounded-full items-center py-4 font-bold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('wallets.add_title')}
          </button>
        </div>
      )}

      {mode !== 'choose' && (
        <button
          onClick={() => setMode('choose')}
          className="text-text-secondary text-sm font-medium self-center"
        >
          {t('common.back')}
        </button>
      )}

      <Snackbar
        message={snackbar.message}
        isOpen={snackbar.isOpen}
        type={snackbar.type}
        onClose={() => setSnackbar((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  )
}

export default AddWallet
